<template>
  <div
    class="location-page"
    @dblclick.capture.prevent.stop
    @touchend.capture="preventDoubleTapZoom"
    @gesturestart.prevent
  >
    <section class="map-panel">
      <div ref="mapContainer" class="map-container"></div>
      <div v-if="mapError" class="map-error">{{ mapError }}</div>
    </section>

    <section class="top-overlay">
      <form class="search-row" @submit.prevent="searchPlaces">
        <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5"/><path d="m16 16 4 4"/></svg>
        <input
          v-model.trim="keyword"
          type="text"
          inputmode="search"
          enterkeyhint="search"
          autocomplete="off"
          autocorrect="off"
          placeholder="搜索地点或粘贴坐标"
        >
        <span v-if="isSearching" class="search-loading">搜索中</span>
        <button type="submit" class="search-submit-button" :disabled="isSearching">搜索</button>
      </form>
      <div v-if="searchResults.length" class="search-results">
        <button
          v-for="place in searchResults"
          :key="place.id || `${place.location}-${place.name}`"
          type="button"
          class="result-item"
          @click="selectSearchResult(place)"
        >
          <strong>{{ place.name }}</strong>
          <span>{{ place.address || place.pname + place.cityname + place.adname }}</span>
        </button>
      </div>

      <div class="toolbar-row">
        <button type="button" :class="['status-button', locationEnabled ? 'enabled' : 'ready']" :disabled="isVerifying" @click="checkLocationState">
          <i></i>{{ isVerifying ? '验证中' : (locationEnabled ? '验证定位' : '就绪') }}<b>›</b>
        </button>
        <div class="round-actions">
          <button type="button" aria-label="返回" @click="goBack">‹</button>
          <button type="button" aria-label="恢复真实定位" :disabled="isResetting || !session" @click="resetLocation">↺</button>
          <a
            class="coordinate-picker-link"
            href="https://lbs.amap.com/tools/picker"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="打开 GCJ-02 坐标拾取器"
            title="坐标拾取"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="6"/>
              <path d="M12 2v4M12 18v4M2 12h4M18 12h4"/>
              <circle cx="12" cy="12" r="1.5"/>
            </svg>
          </a>
          <button
            type="button"
            :class="['coordinate-guide-button', { 'is-highlighted': coordinateGuideHighlighted }]"
            aria-label="查看坐标选点使用说明"
            title="坐标选点说明"
            @click="openCoordinateGuide"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 3.5h8l4 4V20.5H6z"/>
              <path d="M14 3.5v4h4M9 12h6M9 15.5h6"/>
            </svg>
          </button>
        </div>
      </div>
    </section>

    <button type="button" class="locate-button" aria-label="定位当前位置" @click="locateCurrent">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.4 3.7 3.8 10.5c-.8.3-.8 1.5.1 1.7l7 1.7 1.7 7c.2.9 1.4.9 1.7.1l6.8-16.6c.2-.5-.2-.9-.7-.7Z"/></svg>
    </button>

    <section class="bottom-sheet">
      <div class="selection-icon">⌖</div>
      <div class="selection-copy">
        <strong>{{ selectedAddress || '选择位置' }}</strong>
        <span>{{ selectedAddress ? '确认地点后锁定位置' : '在上方搜索，或点击地图任意位置。' }}</span>
      </div>
      <button
        v-if="selectedGcj"
        type="button"
        class="lock-button"
        :disabled="isSubmitting || !session"
        @click="lockLocation"
      >{{ isSubmitting ? '锁定中' : '锁定' }}</button>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import { Toast } from 'vant'
import { getCardSession } from './card-session'
import { verifyVpnConnection } from './vpn-access'

const DEFAULT_CENTER = [116.397428, 39.90923]
const SEARCH_CACHE_TTL = 10 * 60 * 1000
const REVERSE_CACHE_TTL = 30 * 60 * 1000
const AUTO_VERIFY_INTERVAL = 45 * 1000
const AUTO_VERIFY_RESUME_DELAY = 2500
const placeSearchCache = new Map()
const reverseGeocodeCache = new Map()
let amapLoaderPromise = null

function readFreshCache(cache, key, ttl) {
  const cached = cache.get(key)
  if (!cached || Date.now() - cached.savedAt > ttl) {
    cache.delete(key)
    return null
  }
  return cached.value
}

function saveLimitedCache(cache, key, value, limit) {
  cache.set(key, { value, savedAt: Date.now() })
  if (cache.size > limit) cache.delete(cache.keys().next().value)
}

function loadAmap() {
  if (window.AMap) return Promise.resolve(window.AMap)
  if (amapLoaderPromise) return amapLoaderPromise
  const key = process.env.VUE_APP_AMAP_JS_KEY
  if (!key) return Promise.reject(new Error('未配置高德地图 JS Key'))
  const securityCode = process.env.VUE_APP_AMAP_SECURITY_CODE
  if (securityCode) window._AMapSecurityConfig = { securityJsCode: securityCode }
  amapLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://webapi.amap.com/maps?v=2.0&key=${encodeURIComponent(key)}`
    script.async = true
    script.onload = () => (window.AMap ? resolve(window.AMap) : reject(new Error('高德地图加载失败')))
    script.onerror = () => reject(new Error('高德地图加载失败'))
    document.head.appendChild(script)
  })
  return amapLoaderPromise
}

function outOfChina(lat, lng) {
  return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271
}

function transformLat(x, y) {
  let value = -100 + 2 * x + 3 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x))
  value += (20 * Math.sin(6 * x * Math.PI) + 20 * Math.sin(2 * x * Math.PI)) * 2 / 3
  value += (20 * Math.sin(y * Math.PI) + 40 * Math.sin(y / 3 * Math.PI)) * 2 / 3
  value += (160 * Math.sin(y / 12 * Math.PI) + 320 * Math.sin(y * Math.PI / 30)) * 2 / 3
  return value
}

function transformLng(x, y) {
  let value = 300 + x + 2 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x))
  value += (20 * Math.sin(6 * x * Math.PI) + 20 * Math.sin(2 * x * Math.PI)) * 2 / 3
  value += (20 * Math.sin(x * Math.PI) + 40 * Math.sin(x / 3 * Math.PI)) * 2 / 3
  value += (150 * Math.sin(x / 12 * Math.PI) + 300 * Math.sin(x / 30 * Math.PI)) * 2 / 3
  return value
}

function wgsToGcj(lat, lng) {
  if (outOfChina(lat, lng)) return { lat, lng }
  const earthRadius = 6378245
  const eccentricity = 0.006693421622965943
  let deltaLat = transformLat(lng - 105, lat - 35)
  let deltaLng = transformLng(lng - 105, lat - 35)
  const radLat = lat / 180 * Math.PI
  let magic = Math.sin(radLat)
  magic = 1 - eccentricity * magic * magic
  const sqrtMagic = Math.sqrt(magic)
  deltaLat = deltaLat * 180 / ((earthRadius * (1 - eccentricity)) / (magic * sqrtMagic) * Math.PI)
  deltaLng = deltaLng * 180 / (earthRadius / sqrtMagic * Math.cos(radLat) * Math.PI)
  return { lat: lat + deltaLat, lng: lng + deltaLng }
}

function gcjToWgs(lat, lng) {
  if (outOfChina(lat, lng)) return { lat, lng }
  let wgsLat = lat
  let wgsLng = lng
  for (let index = 0; index < 3; index += 1) {
    const converted = wgsToGcj(wgsLat, wgsLng)
    wgsLat += lat - converted.lat
    wgsLng += lng - converted.lng
  }
  return { lat: wgsLat, lng: wgsLng }
}

export default {
  name: 'IphonLocalMap',
  data() {
    return {
      session: null,
      map: null,
      marker: null,
      keyword: '',
      searchResults: [],
      coordinateGuideHighlighted: false,
      selectionSource: 'MAP',
      selectedGcj: null,
      selectedAddress: '',
      altitude: 0,
      horizontalAccuracy: 39,
      verticalAccuracy: 1000,
      locationEnabled: false,
      isSearching: false,
      isSubmitting: false,
      isResetting: false,
      isLoadingStatus: false,
      isVerifying: false,
      mapError: '',
      reverseGeocodeTimer: null,
      autoVerifyTimer: null,
      autoVerifyDelayTimer: null,
      lastTouchEndAt: 0,
    }
  },
  computed: {
    selectedWgs() {
      if (!this.selectedGcj) return null
      return gcjToWgs(this.selectedGcj.lat, this.selectedGcj.lng)
    },
    statusText() {
      if (this.isLoadingStatus) return '正在读取定位状态'
      return this.locationEnabled ? '设备正在使用模拟位置' : '当前已恢复真实定位'
    },
  },
  async mounted() {
    this.session = getCardSession()
    if (!this.session) {
      this.$router.replace('/iphon-seting/auth')
      return
    }
    try {
      // 路由守卫之外再做一次组件级校验，避免直接复用组件或异常导航绕过 VPN 检查。
      await verifyVpnConnection(this.session)
      await this.initializeMap()
      await this.refreshStatus()
      this.startAutoVerification()
    } catch (error) {
      this.mapError = error.message || '地图初始化失败'
      Toast.fail(this.mapError)
      if (this.mapError.includes('VPN')) this.$router.replace('/iphon-seting')
    }
  },
  beforeDestroy() {
    if (this.reverseGeocodeTimer) clearTimeout(this.reverseGeocodeTimer)
    this.stopAutoVerification()
    if (this.map) this.map.destroy()
  },
  methods: {
    preventDoubleTapZoom(event) {
      const now = Date.now()
      if (now - this.lastTouchEndAt < 360) event.preventDefault()
      this.lastTouchEndAt = now
    },
    goBack() {
      this.$router.push('/iphon-seting')
    },
    openCoordinateGuide() {
      this.coordinateGuideHighlighted = false
      const target = this.$router.resolve('/iphon-seting/coordinate-guide')
      window.open(target.href, '_blank', 'noopener,noreferrer')
    },
    async initializeMap() {
      const AMap = await loadAmap()
      this.map = new AMap.Map(this.$refs.mapContainer, {
        zoom: 13,
        center: DEFAULT_CENTER,
        viewMode: '2D',
        doubleClickZoom: false,
      })
      this.marker = new AMap.Marker({
        position: DEFAULT_CENTER,
        draggable: true,
        offset: new AMap.Pixel(-38, -38),
        content: `
          <div class="glin-location-target" aria-label="当前选择位置">
            <span class="glin-location-target__pulse"></span>
            <span class="glin-location-target__dot"><i></i></span>
          </div>
        `,
      })
      this.map.add(this.marker)
      this.map.on('click', event => this.chooseGcjLocation(event.lnglat.getLng(), event.lnglat.getLat(), true, 'MAP'))
      this.marker.on('dragend', event => this.chooseGcjLocation(event.lnglat.getLng(), event.lnglat.getLat(), true, 'MAP'))
    },
    ensureSuccess(response, fallbackMessage) {
      const payload = response && response.data
      if (!payload || payload.code !== 200) throw new Error((payload && payload.msg) || fallbackMessage)
      return payload.data
    },
    async refreshStatus() {
      if (!this.session) return
      this.isLoadingStatus = true
      try {
        const response = await axios.get('/api/vpn/card/location/status', {
          params: { cardKey: this.session.cardKey, deviceId: this.session.deviceId },
          timeout: 10000,
        })
        const location = this.ensureSuccess(response, '读取定位状态失败')
        this.locationEnabled = location.enabled === true || location.enabled === 1 || location.enabled === '1'
        if (this.locationEnabled && Number.isFinite(Number(location.latitude)) && Number.isFinite(Number(location.longitude))) {
          const gcj = wgsToGcj(Number(location.latitude), Number(location.longitude))
          this.chooseGcjLocation(gcj.lng, gcj.lat, false)
          this.altitude = Number(location.altitude) || 0
          this.horizontalAccuracy = Number(location.horizontalAccuracy) || 39
          this.verticalAccuracy = Number(location.verticalAccuracy) || 1000
        } else if (!this.locationEnabled) {
          // 真实定位模式不能继续展示该卡密历史保存的模拟坐标。
          this.selectedGcj = null
          this.selectedAddress = ''
          this.positionAtCurrentLocation(true)
        }
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.isLoadingStatus = false
      }
    },
    async checkLocationState() {
      await this.refreshStatus()
      if (this.locationEnabled) this.verifyActualLocation(false)
    },
    startAutoVerification() {
      if (this.autoVerifyTimer) return
      document.addEventListener('visibilitychange', this.handleAutoVerifyResume)
      window.addEventListener('focus', this.handleAutoVerifyResume)
      window.addEventListener('pageshow', this.handleAutoVerifyResume)
      this.autoVerifyTimer = window.setInterval(() => {
        if (document.visibilityState === 'visible') this.verifyActualLocation(true)
      }, AUTO_VERIFY_INTERVAL)
      // 已经锁定位置的用户进入地图后无需点击按钮，自动读取一次实际坐标。
      this.scheduleAutoVerification(AUTO_VERIFY_RESUME_DELAY)
    },
    stopAutoVerification() {
      if (this.autoVerifyTimer) window.clearInterval(this.autoVerifyTimer)
      if (this.autoVerifyDelayTimer) window.clearTimeout(this.autoVerifyDelayTimer)
      this.autoVerifyTimer = null
      this.autoVerifyDelayTimer = null
      document.removeEventListener('visibilitychange', this.handleAutoVerifyResume)
      window.removeEventListener('focus', this.handleAutoVerifyResume)
      window.removeEventListener('pageshow', this.handleAutoVerifyResume)
    },
    handleAutoVerifyResume() {
      if (document.visibilityState !== 'visible') return
      // 用户从“设置”切回 Safari 后稍等定位服务稳定，再自动验证。
      this.scheduleAutoVerification(AUTO_VERIFY_RESUME_DELAY)
    },
    scheduleAutoVerification(delay = AUTO_VERIFY_RESUME_DELAY) {
      if (this.autoVerifyDelayTimer) window.clearTimeout(this.autoVerifyDelayTimer)
      if (!this.locationEnabled || !this.session) return
      this.autoVerifyDelayTimer = window.setTimeout(() => {
        this.autoVerifyDelayTimer = null
        if (document.visibilityState === 'visible') this.verifyActualLocation(true)
      }, delay)
    },
    verifyActualLocation(silent) {
      if (!navigator.geolocation || !this.session || !this.locationEnabled || this.isVerifying) return
      this.isVerifying = true
      navigator.geolocation.getCurrentPosition(async position => {
        try {
          const response = await axios.post('/api/vpn/card/location/verify', {
            ...this.requestBody(),
            latitude: Number(position.coords.latitude),
            longitude: Number(position.coords.longitude),
            accuracy: Number(position.coords.accuracy) || 0,
          }, { timeout: 12000 })
          const result = this.ensureSuccess(response, '定位验证失败')
          if (!silent) {
            if (result.status === 'VERIFIED') {
              Toast.success(`定位验证成功，偏差约 ${result.distanceMeters || 0} 米`)
            } else if (result.status === 'VERIFYING') {
              Toast('检测到位置偏差，正在自动进行二次确认')
            } else {
              Toast.fail(`实际位置未到达目标点，偏差约 ${result.distanceMeters || 0} 米`)
            }
          }
          // 第一次偏离不立即判失败，短暂等待后自动获取第二个样本。
          if (result.status === 'VERIFYING') this.scheduleAutoVerification(8000)
        } catch (error) {
          if (!silent) this.handleRequestError(error)
        } finally {
          this.isVerifying = false
        }
      }, () => {
        this.isVerifying = false
        if (!silent) Toast.fail('无法读取实际位置，请允许 Safari 使用定位服务')
      }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 })
    },
    chooseGcjLocation(lng, lat, resolveAddress, source = 'MAP') {
      const longitude = Number(lng)
      const latitude = Number(lat)
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return
      this.selectedGcj = { lng: longitude, lat: latitude }
      this.selectionSource = source
      this.marker.setPosition([longitude, latitude])
      this.map.setCenter([longitude, latitude])
      this.searchResults = []
      if (resolveAddress) this.scheduleReverseGeocode()
    },
    scheduleReverseGeocode() {
      if (this.reverseGeocodeTimer) clearTimeout(this.reverseGeocodeTimer)
      this.reverseGeocodeTimer = setTimeout(() => this.reverseGeocode(), 450)
    },
    async reverseGeocode() {
      if (!this.selectedGcj) return
      const cacheKey = `${this.selectedGcj.lng.toFixed(4)},${this.selectedGcj.lat.toFixed(4)}`
      const cachedAddress = readFreshCache(reverseGeocodeCache, cacheKey, REVERSE_CACHE_TTL)
      if (cachedAddress) {
        this.selectedAddress = cachedAddress
        return
      }
      try {
        const response = await axios.get('/api/vpn/card/map/regeo', {
          params: { ...this.selectedGcj, ...this.requestBody() },
          timeout: 10000,
        })
        const data = this.ensureSuccess(response, '地址解析失败')
        this.selectedAddress = (data && data.regeocode && data.regeocode.formatted_address) || '地图选定位置'
        saveLimitedCache(reverseGeocodeCache, cacheKey, this.selectedAddress, 60)
      } catch (error) {
        const payload = error && error.response && error.response.data
        const message = (payload && payload.msg) || error.message || ''
        if (message.includes('VPN')) {
          this.handleRequestError(error)
          return
        }
        this.selectedAddress = '地图选定位置'
      }
    },
    async searchPlaces() {
      const keywords = this.keyword.replace(/\s+/g, ' ').trim()
      const coordinate = this.parseCoordinateInput(keywords)
      if (coordinate) {
        this.searchByCoordinate(keywords, coordinate)
        return
      }
      if (keywords.length < 2) {
        Toast('请输入至少两个字')
        return
      }
      const cacheKey = keywords.toLowerCase()
      const cachedResults = readFreshCache(placeSearchCache, cacheKey, SEARCH_CACHE_TTL)
      if (cachedResults) {
        this.searchResults = cachedResults
        if (!cachedResults.length) this.showCoordinateGuideHint('没有找到相关地点')
        return
      }
      this.isSearching = true
      try {
        const response = await axios.get('/api/vpn/card/map/search', {
          params: { keywords, ...this.requestBody() },
          timeout: 10000,
        })
        const data = this.ensureSuccess(response, '地点搜索失败')
        this.searchResults = (data && data.pois) || []
        saveLimitedCache(placeSearchCache, cacheKey, this.searchResults, 30)
        if (!this.searchResults.length) this.showCoordinateGuideHint('没有找到相关地点')
      } catch (error) {
        this.coordinateGuideHighlighted = true
        this.handleRequestError(error)
      } finally {
        this.isSearching = false
      }
    },
    selectSearchResult(place) {
      const values = String(place.location || '').split(',').map(Number)
      if (values.length !== 2 || values.some(value => !Number.isFinite(value))) {
        Toast.fail('该地点没有可用坐标')
        return
      }
      this.selectedAddress = [place.name, place.address].filter(Boolean).join(' · ')
      this.chooseGcjLocation(values[0], values[1], false, 'SEARCH')
      this.map.setZoom(17)
    },
    showCoordinateGuideHint(message) {
      this.coordinateGuideHighlighted = true
      Toast(`${message}，可点击右上角文档查看坐标选点方法`)
    },
    parseCoordinateInput(value) {
      const match = String(value || '').trim().match(/^\s*\(?\s*([+-]?\d+(?:\.\d+)?)\s*[,，\s]\s*([+-]?\d+(?:\.\d+)?)\s*\)?\s*$/)
      if (!match) return null
      const first = Number(match[1])
      const second = Number(match[2])
      if (!Number.isFinite(first) || !Number.isFinite(second)) return null

      // 常见坐标既可能是“经度,纬度”，也可能由手机复制成“纬度,经度”。
      let lng = first
      let lat = second
      if (Math.abs(first) <= 90 && Math.abs(second) > 90) {
        lng = second
        lat = first
      } else if (Math.abs(first) <= 55 && Math.abs(second) >= 72 && Math.abs(second) <= 138) {
        lng = second
        lat = first
      }
      if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return null
      return { lng, lat }
    },
    searchByCoordinate(keywords, parsedCoordinate) {
      const coordinate = parsedCoordinate || this.parseCoordinateInput(keywords)
      if (!coordinate) {
        Toast('请输入“经度, 纬度”，例如 116.397, 39.909')
        return
      }
      this.selectedAddress = `坐标位置 ${coordinate.lng.toFixed(6)}, ${coordinate.lat.toFixed(6)}`
      this.chooseGcjLocation(coordinate.lng, coordinate.lat, false, 'COORDINATE')
      this.map.setZoom(17)
      Toast.success('已按坐标定位')
    },
    locateCurrent() {
      this.positionAtCurrentLocation(false)
    },
    positionAtCurrentLocation(silent) {
      if (!navigator.geolocation) {
        if (!silent) Toast.fail('当前浏览器不支持定位')
        return
      }
      const loading = silent ? null : Toast.loading({ duration: 0, forbidClick: true, message: '正在定位...' })
      navigator.geolocation.getCurrentPosition(position => {
        if (loading) loading.clear()
        if (this.locationEnabled) {
          // 用户点击当前位置时顺便把 CoreLocation 实际坐标上报用于后台验证。
          this.reportPositionVerification(position)
        }
        const gcj = wgsToGcj(Number(position.coords.latitude), Number(position.coords.longitude))
        this.chooseGcjLocation(gcj.lng, gcj.lat, true, 'MAP')
        this.map.setZoom(17)
      }, () => {
        if (loading) loading.clear()
        if (!silent) Toast.fail('无法获取当前位置，请检查 Safari 定位权限')
      }, { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 })
    },
    async reportPositionVerification(position) {
      try {
        await axios.post('/api/vpn/card/location/verify', {
          ...this.requestBody(),
          latitude: Number(position.coords.latitude),
          longitude: Number(position.coords.longitude),
          accuracy: Number(position.coords.accuracy) || 0,
        }, { timeout: 12000 })
      } catch (error) {
        // 地图定位按钮本身不应因监控上报失败而不可用。
      }
    },
    requestBody() {
      return { cardKey: this.session.cardKey, deviceId: this.session.deviceId }
    },
    async lockLocation() {
      if (!this.selectedWgs) return
      this.isSubmitting = true
      try {
        const response = await axios.post('/api/vpn/card/location/lock', {
          ...this.requestBody(),
          latitude: this.selectedWgs.lat,
          longitude: this.selectedWgs.lng,
          altitude: this.altitude,
          horizontalAccuracy: this.horizontalAccuracy,
          verticalAccuracy: this.verticalAccuracy,
          addressName: this.selectedAddress,
          locationSource: this.selectionSource,
        }, { timeout: 12000 })
        this.ensureSuccess(response, '位置锁定失败')
        this.locationEnabled = true
        // 用户无需主动点击验证；从系统设置返回或停留在页面时会自动检测。
        this.scheduleAutoVerification(10000)
        Toast.success('位置已锁定，请刷新手机定位服务')
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.isSubmitting = false
      }
    },
    async resetLocation() {
      this.isResetting = true
      try {
        const response = await axios.post('/api/vpn/card/location/reset', this.requestBody(), { timeout: 12000 })
        this.ensureSuccess(response, '恢复真实定位失败')
        this.locationEnabled = false
        if (this.autoVerifyDelayTimer) window.clearTimeout(this.autoVerifyDelayTimer)
        this.autoVerifyDelayTimer = null
        this.selectedGcj = null
        this.selectedAddress = ''
        this.positionAtCurrentLocation(true)
        Toast.success('已恢复真实定位，请刷新手机定位服务')
      } catch (error) {
        this.handleRequestError(error)
      } finally {
        this.isResetting = false
      }
    },
    handleRequestError(error) {
      const payload = error && error.response && error.response.data
      const message = (payload && payload.msg) || error.message || '操作失败，请稍后重试'
      Toast.fail(message)
      if (message.includes('卡密') || message.includes('设备')) {
        this.$router.replace('/iphon-seting/auth')
      } else if (message.includes('VPN')) {
        this.$router.replace('/iphon-seting')
      }
    },
  },
}
</script>

<style scoped>
.location-page {
  min-height: 100vh;
  min-height: 100dvh;
  padding: max(10px, env(safe-area-inset-top)) 10px calc(84px + env(safe-area-inset-bottom));
  overflow-x: hidden;
  background: #f5f7fa;
  color: #182230;
  box-sizing: border-box;
  overscroll-behavior: none;
  -webkit-text-size-adjust: 100%;
}
.page-header {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: center;
  min-height: 42px;
  margin-bottom: 8px;
}
.page-header h1 { margin: 0; font-size: 18px; font-weight: 700; text-align: center; }
.icon-button {
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #fff;
  color: #1f6fc1;
  font-size: 30px;
  line-height: 32px;
}
.status-pill { padding: 5px 9px; border-radius: 999px; font-size: 11px; white-space: nowrap; }
.status-pill.enabled { background: #e5f7ed; color: #18794e; }
.status-pill.disabled { background: #e9eef5; color: #5f6b7a; }
.search-panel, .map-panel, .selection-card { margin-bottom: 8px; border-radius: 15px; background: #fff; overflow: hidden; }
.search-panel { position: relative; z-index: 20; padding: 8px; }
.search-row { display: flex; gap: 7px; }
.search-row input {
  min-width: 0;
  flex: 1;
  height: 42px;
  padding: 0 13px;
  border: 1px solid #e0e6ed;
  border-radius: 11px;
  outline: none;
  background: #f7f9fb;
  font-size: 16px;
}
.search-row input:focus { border-color: #5c9cdb; background: #fff; }
.search-row button {
  min-width: 62px;
  padding: 0 13px;
  border: 0;
  border-radius: 11px;
  background: #2478cc;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}
.search-results { max-height: min(210px, 30dvh); margin-top: 7px; overflow-y: auto; border-top: 1px solid #edf1f5; -webkit-overflow-scrolling: touch; }
.result-item { display: block; width: 100%; padding: 10px 5px; border: 0; border-bottom: 1px solid #edf1f5; background: transparent; text-align: left; }
.result-item strong, .result-item span { display: block; }
.result-item strong { font-size: 14px; }
.result-item span { margin-top: 3px; overflow: hidden; color: #7a8696; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.map-panel { position: relative; }
.map-container { height: clamp(320px, 54dvh, 620px); }
.locate-button {
  position: absolute;
  right: 12px;
  bottom: 12px;
  z-index: 10;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 1px solid #dce4ec;
  border-radius: 50%;
  background: #fff;
  color: #2478cc;
  box-shadow: 0 4px 14px rgba(34, 60, 88, .16);
  font-size: 22px;
}
.map-error { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; padding: 24px; background: #fff; color: #c74444; text-align: center; }
.selection-card { display: flex; align-items: center; gap: 10px; min-height: 48px; padding: 10px 13px; }
.selection-card span { flex: 0 0 auto; color: #8994a3; font-size: 11px; }
.selection-card strong { min-width: 0; overflow: hidden; font-size: 14px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.action-bar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 50;
  display: grid;
  grid-template-columns: minmax(105px, .7fr) minmax(150px, 1.3fr);
  gap: 9px;
  padding: 10px max(12px, env(safe-area-inset-right)) calc(10px + env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left));
  border-top: 1px solid rgba(221, 228, 236, .9);
  background: rgba(255, 255, 255, .94);
  box-shadow: 0 -5px 18px rgba(28, 47, 68, .08);
  -webkit-backdrop-filter: blur(16px);
  backdrop-filter: blur(16px);
}
.primary-action, .secondary-action { min-height: 46px; border: 0; border-radius: 13px; font-size: 15px; font-weight: 700; }
.primary-action { background: #2478cc; color: #fff; }
.secondary-action { background: #edf2f7; color: #42526a; }
button { touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
button:disabled { opacity: .45; }
@media (max-height: 700px) {
  .map-container { height: 48dvh; min-height: 280px; }
  .selection-card { min-height: 42px; padding-top: 8px; padding-bottom: 8px; }
}

/* 沉浸式地图布局：地图铺满屏幕，控件只作为轻量浮层出现。 */
.location-page {
  position: relative;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  background: #dce9d2;
}
.map-panel {
  position: absolute;
  inset: 0;
  margin: 0;
  border-radius: 0;
  background: #dce9d2;
  overflow: hidden;
}
.map-container { width: 100%; height: 100%; min-height: 0; }
.top-overlay {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 30;
  padding: calc(12px + env(safe-area-inset-top)) 16px 0;
  pointer-events: none;
}
.search-row {
  display: flex;
  align-items: center;
  height: 58px;
  padding: 0 17px;
  border: 1px solid rgba(255, 255, 255, .68);
  border-radius: 22px;
  background: rgba(255, 255, 255, .9);
  box-shadow: 0 8px 28px rgba(35, 58, 43, .12);
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
  pointer-events: auto;
}
.search-row svg {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  margin-right: 11px;
  fill: none;
  stroke: #637066;
  stroke-linecap: round;
  stroke-width: 1.8;
}
.search-row input {
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #1d2820;
  font-size: 16px;
}
.search-row input::-webkit-search-cancel-button { opacity: .55; }
.search-loading { flex: 0 0 auto; color: #7b877d; font-size: 11px; }
.search-row .search-submit-button {
  flex: 0 0 auto;
  min-width: 52px;
  height: 32px;
  margin-left: 8px;
  padding: 0 10px;
  border: 0;
  border-radius: 999px;
  background: #1684e8;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 32px;
  white-space: nowrap;
}
.search-row .search-submit-button:active { background: #0874d6; }
.search-row .search-submit-button:disabled { opacity: .55; }
.search-results {
  max-height: min(260px, 36dvh);
  margin: 7px 3px 0;
  padding: 5px 12px;
  border: 0;
  border-radius: 18px;
  background: rgba(255, 255, 255, .94);
  box-shadow: 0 10px 28px rgba(34, 52, 39, .16);
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
  pointer-events: auto;
}
.result-item { padding: 11px 3px; }
.toolbar-row { display: flex; justify-content: space-between; align-items: center; margin-top: 12px; }
.status-button {
  display: flex;
  align-items: center;
  height: 45px;
  padding: 0 15px;
  border: 1px solid rgba(255, 255, 255, .7);
  border-radius: 23px;
  background: rgba(255, 255, 255, .9);
  box-shadow: 0 6px 20px rgba(34, 52, 39, .1);
  color: #17231a;
  font-size: 14px;
  font-weight: 700;
  pointer-events: auto;
}
.status-button i { width: 9px; height: 9px; margin-right: 9px; border-radius: 50%; background: #2bc968; }
.status-button.enabled i { background: #ff9f2f; }
.status-button b { margin-left: 8px; color: #737e75; font-size: 20px; font-weight: 400; }
.round-actions { display: flex; gap: 9px; pointer-events: auto; }
.round-actions button,
.round-actions a {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, .7);
  border-radius: 50%;
  background: rgba(255, 255, 255, .9);
  box-shadow: 0 6px 20px rgba(34, 52, 39, .1);
  color: #17231a;
  font-size: 24px;
  font-weight: 600;
  box-sizing: border-box;
  text-decoration: none;
}
.round-actions button:first-child { padding-bottom: 4px; font-size: 31px; font-weight: 400; }
.round-actions .coordinate-picker-link svg {
  width: 23px;
  height: 23px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-width: 1.8;
}
.round-actions .coordinate-guide-button svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
}
.round-actions .coordinate-guide-button.is-highlighted {
  border-color: rgba(255, 159, 47, .9);
  background: #fff7e8;
  color: #e87912;
  animation: coordinate-guide-glow 1.25s ease-in-out infinite;
}
@keyframes coordinate-guide-glow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255, 145, 28, .18), 0 6px 20px rgba(34, 52, 39, .1); }
  50% { box-shadow: 0 0 0 7px rgba(255, 145, 28, .18), 0 6px 20px rgba(34, 52, 39, .1); }
}
@media (max-width: 360px) {
  .round-actions { gap: 6px; }
  .round-actions button,
  .round-actions a { width: 41px; height: 41px; }
  .status-button { height: 41px; padding: 0 12px; }
}
@media (prefers-reduced-motion: reduce) {
  .round-actions .coordinate-guide-button.is-highlighted { animation: none; }
}
.locate-button {
  position: absolute;
  right: 18px;
  bottom: calc(158px + env(safe-area-inset-bottom));
  z-index: 25;
  width: 52px;
  height: 52px;
  border: 1px solid rgba(255, 255, 255, .75);
  background: rgba(255, 255, 255, .94);
  box-shadow: 0 8px 24px rgba(26, 60, 88, .18);
}
.locate-button svg { width: 25px; height: 25px; fill: #1684e8; }
.bottom-sheet {
  position: absolute;
  right: 16px;
  bottom: calc(34px + env(safe-area-inset-bottom));
  left: 16px;
  z-index: 25;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 88px;
  padding: 13px 15px;
  border: 1px solid rgba(255, 255, 255, .72);
  border-radius: 25px;
  background: rgba(255, 255, 255, .9);
  box-shadow: 0 10px 32px rgba(28, 55, 72, .14);
  box-sizing: border-box;
  -webkit-backdrop-filter: blur(20px);
  backdrop-filter: blur(20px);
}
.selection-icon { color: #1684e8; font-size: 29px; text-align: center; }
.selection-copy { min-width: 0; }
.selection-copy strong, .selection-copy span { display: block; }
.selection-copy strong {
  max-height: 48px;
  overflow-x: hidden;
  overflow-y: auto;
  color: #16231b;
  font-size: 16px;
  line-height: 24px;
  overflow-wrap: anywhere;
  -webkit-overflow-scrolling: touch;
}
.selection-copy strong::-webkit-scrollbar { width: 0; height: 0; }
.selection-copy span { margin-top: 5px; color: #718078; font-size: 12px; }
.lock-button {
  min-width: 62px;
  height: 42px;
  padding: 0 14px;
  border: 0;
  border-radius: 15px;
  background: #1684e8;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}
.map-error { z-index: 40; }
@media (max-height: 700px) {
  .map-container { height: 100%; min-height: 0; }
  .search-row { height: 52px; }
  .toolbar-row { margin-top: 8px; }
  .bottom-sheet { min-height: 78px; }
  .locate-button { bottom: calc(142px + env(safe-area-inset-bottom)); }
  .bottom-sheet { bottom: calc(28px + env(safe-area-inset-bottom)); }
}
</style>

<style>
.glin-location-target {
  position: relative;
  width: 76px;
  height: 76px;
  pointer-events: none;
  filter: drop-shadow(0 3px 5px rgba(177, 24, 24, .22));
}
.glin-location-target::before,
.glin-location-target::after {
  position: absolute;
  z-index: 1;
  content: '';
  background: #f04444;
  border-radius: 2px;
}
.glin-location-target::before {
  top: 37px;
  left: 1px;
  width: 74px;
  height: 2px;
}
.glin-location-target::after {
  top: 1px;
  left: 37px;
  width: 2px;
  height: 74px;
}
.glin-location-target__pulse {
  position: absolute;
  top: 22px;
  left: 22px;
  z-index: 2;
  width: 32px;
  height: 32px;
  border: 2px solid rgba(240, 68, 68, .4);
  border-radius: 50%;
  box-sizing: border-box;
  animation: glin-location-pulse 1.8s ease-out infinite;
}
.glin-location-target__dot {
  position: absolute;
  top: 27px;
  left: 27px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 3px solid #fff;
  border-radius: 50%;
  background: #ef3f3f;
  box-shadow: 0 0 0 2px rgba(213, 35, 35, .2);
  box-sizing: border-box;
}
.glin-location-target__dot i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #fff;
}
@keyframes glin-location-pulse {
  0% { opacity: .9; transform: scale(.65); }
  75%, 100% { opacity: 0; transform: scale(1.45); }
}
@media (prefers-reduced-motion: reduce) {
  .glin-location-target__pulse { animation: none; }
}
</style>
